import { useCallback, useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    grecaptcha?: {
      render: (
        container: string | HTMLElement,
        parameters: {
          sitekey: string;
          size?: 'invisible' | 'normal' | 'compact';
          badge?: 'bottomright' | 'bottomleft' | 'inline';
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
        },
        inherit?: boolean,
      ) => number;
      execute: (widgetId?: number) => void;
      reset: (widgetId?: number) => void;
      ready: (cb: () => void) => void;
    };
    __recaptchaPromise?: Promise<void>;
  }
}

type RecaptchaStatus = 'idle' | 'loading' | 'ready' | 'error';

const RECAPTCHA_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';

const loadRecaptcha = () => {
  if (!window.__recaptchaPromise) {
    window.__recaptchaPromise = new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>(
        `script[src^=\"${RECAPTCHA_SRC}\"]`,
      );
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve());
        existingScript.addEventListener('error', () =>
          reject(new Error('Failed to load reCAPTCHA')),
        );
        return;
      }

      const script = document.createElement('script');
      script.src = RECAPTCHA_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
      document.head.appendChild(script);
    });
  }

  return window.__recaptchaPromise;
};

export function useRecaptcha(siteKey: string | undefined, containerId: string) {
  const [status, setStatus] = useState<RecaptchaStatus>(siteKey ? 'loading' : 'error');
  const [error, setError] = useState<string | null>(siteKey ? null : 'Missing reCAPTCHA site key');
  const widgetIdRef = useRef<number | null>(null);
  const resolveRef = useRef<((token: string) => void) | null>(null);
  const rejectRef = useRef<((error: Error) => void) | null>(null);

  useEffect(() => {
    if (!siteKey) {
      setStatus('error');
      setError('Missing reCAPTCHA site key');
      return;
    }

    loadRecaptcha()
      ?.then(() => {
        if (window.grecaptcha?.ready) {
          window.grecaptcha.ready(() => setStatus('ready'));
        } else {
          setStatus('error');
          setError('reCAPTCHA unavailable after load');
        }
      })
      .catch((err: Error) => {
        setStatus('error');
        setError(err.message);
      });

    return () => {
      if (widgetIdRef.current !== null) {
        window.grecaptcha?.reset(widgetIdRef.current);
      }
    };
  }, [siteKey]);

  const ensureWidget = useCallback(() => {
    if (!siteKey) {
      throw new Error('Missing reCAPTCHA site key');
    }
    if (!window.grecaptcha?.render) {
      throw new Error('reCAPTCHA not ready yet');
    }
    if (widgetIdRef.current !== null) {
      return widgetIdRef.current;
    }

    const widgetId = window.grecaptcha.render(containerId, {
      sitekey: siteKey,
      size: 'invisible',
      badge: 'bottomright',
      callback: (token: string) => resolveRef.current?.(token),
      'error-callback': () => rejectRef.current?.(new Error('reCAPTCHA failed')),
      'expired-callback': () => rejectRef.current?.(new Error('reCAPTCHA expired')),
    });

    widgetIdRef.current = widgetId;
    return widgetId;
  }, [containerId, siteKey]);

  const execute = useCallback(async () => {
    if (!siteKey) {
      throw new Error('Missing reCAPTCHA site key');
    }
    if (!window.grecaptcha?.execute) {
      throw new Error('reCAPTCHA not ready yet');
    }

    await new Promise<void>((resolve) => window.grecaptcha?.ready(resolve));
    const widgetId = ensureWidget();

    return new Promise<string>((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
      window.grecaptcha?.execute(widgetId);
    });
  }, [ensureWidget, siteKey]);

  return { status, error, execute };
}

export default useRecaptcha;
